/**
 * Enrollments Service
 * Handles class enrollment operations and registration
 */

import apiClient from "../client";
import { API_ENDPOINTS } from "../../constants/api.constants";

const enrollmentsService = {
  /**
   * Get current user's enrollments
   * @param {Object} filters - Filter parameters
   * @param {string} [filters.status] - Filter by status (active, completed, cancelled)
   * @param {string} [filters.child_id] - Filter by child ID
   * @returns {Promise<Array>} List of enrollments
   */
  async getMy(filters = {}) {
    const response = await apiClient.get(API_ENDPOINTS.ENROLLMENTS.MY, {
      params: filters,
    });
    const { data } = response;

    // Handle paginated response format {items: [...], total: X}
    if (data && data.items) {
      return data.items;
    }

    // Return data as-is if not paginated (or if it's a direct array)
    return data;
  },

  /**
   * Get enrollment by ID
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} Enrollment details with class and child info
   */
  async getById(id) {
    const { data } = await apiClient.get(API_ENDPOINTS.ENROLLMENTS.BY_ID(id));
    return data;
  },

  /**
   * Create new enrollment (register child for a class)
   * @param {Object} enrollmentData - Enrollment information
   * @param {string} enrollmentData.child_id - Child ID
   * @param {string} enrollmentData.class_id - Class ID
   * @param {string} [enrollmentData.notes] - Additional notes
   * @param {boolean} [enrollmentData.payment_completed] - Payment status
   * @returns {Promise<Object>} Created enrollment
   */
  async create(enrollmentData) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.CREATE,
      enrollmentData,
    );
    return data;
  },

  /**
   * Update enrollment information
   * @param {string} id - Enrollment ID
   * @param {Object} enrollmentData - Updated enrollment data
   * @returns {Promise<Object>} Updated enrollment
   */
  async update(id, enrollmentData) {
    const { data } = await apiClient.put(
      API_ENDPOINTS.ENROLLMENTS.BY_ID(id),
      enrollmentData,
    );
    return data;
  },

  /**
   * Set or clear an enrollment's roster group number (coach/admin)
   * @param {string} id - Enrollment ID
   * @param {number|null} groupNumber - Group number 1-10, or null to unassign
   * @returns {Promise<Object>} Updated enrollment
   */
  async updateGroup(id, groupNumber) {
    const { data } = await apiClient.patch(
      API_ENDPOINTS.ENROLLMENTS.GROUP(id),
      { group_number: groupNumber },
    );
    return data;
  },

  /**
   * Cancel an enrollment as staff (admin/owner).
   *
   * Cancels now, stops membership billing, frees the seat and offers it to
   * the waitlist. It does NOT refund: a refund is a separate, deliberate
   * step. `cancel` below is the PARENT flow and must not be used by staff:
   * for a class that has started or starts within 15 days it only files a
   * request for review, leaving the child enrolled and billed.
   * @param {string} id - Enrollment ID
   * @param {string} [reason] - Why, for the record
   * @returns {Promise<Object>} The cancelled enrollment
   */
  async cancelAsStaff(id, reason = "Cancelled by admin") {
    return this.update(id, {
      status: "cancelled",
      cancellation_reason: reason,
    });
  },

  /**
   * Cancel enrollment
   * @param {string} id - Enrollment ID
   * @param {Object} cancellationData - Cancellation information
   * @param {string} [cancellationData.reason] - Reason for cancellation
   * @returns {Promise<Object>} Cancellation confirmation
   */
  async cancel(id, cancellationData = {}) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.CANCEL(id),
      cancellationData,
    );
    return data;
  },

  /**
   * Get enrollment attendance records
   * @param {string} id - Enrollment ID
   * @returns {Promise<Array>} Attendance records
   */
  async getAttendance(id) {
    const { data } = await apiClient.get(
      API_ENDPOINTS.ENROLLMENTS.ATTENDANCE(id),
    );
    return data;
  },

  /**
   * Check if child can enroll in a class
   * @param {string} childId - Child ID
   * @param {string} classId - Class ID
   * @returns {Promise<Object>} Eligibility info {eligible: boolean, reason: string}
   */
  async checkEligibility(childId, classId) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.CHECK_ELIGIBILITY,
      { child_id: childId, class_id: classId },
    );
    return data;
  },

  /**
   * Get enrollments for a specific child
   * @param {string} childId - Child ID
   * @returns {Promise<Array>} Child's enrollments
   */
  async getByChild(childId) {
    return this.getMy({ child_id: childId });
  },

  /**
   * Get enrollments for a specific class (admin)
   * @param {string} classId - Class ID
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} Class enrollments
   */
  async getByClass(classId, filters = {}) {
    return this.getAll({ class_id: classId, ...filters });
  },

  /**
   * Get active enrollments
   * @returns {Promise<Array>} Active enrollments
   */
  async getActive() {
    return this.getMy({ status: "active" });
  },

  /**
   * Get completed enrollments
   * @returns {Promise<Array>} Completed enrollments
   */
  async getCompleted() {
    return this.getMy({ status: "completed" });
  },

  /**
   * Get cancelled enrollments
   * @returns {Promise<Array>} Cancelled enrollments
   */
  async getCancelled() {
    return this.getMy({ status: "cancelled" });
  },

  // ============== Admin Methods ==============

  /**
   * Get all enrollments (admin only)
   * @param {Object} filters - Filter parameters
   * @param {string} [filters.status] - Filter by status
   * @param {string} [filters.class_id] - Filter by class ID
   * @param {number} [filters.limit] - Limit results
   * @param {number} [filters.offset] - Offset for pagination
   * @returns {Promise<Object>} Response with items and total
   */
  async getAll(filters = {}) {
    const { data } = await apiClient.get(API_ENDPOINTS.ENROLLMENTS.LIST, {
      params: filters,
    });
    return data;
  },

  /**
   * Delete an enrollment (admin only)
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(id) {
    const { data } = await apiClient.delete(
      API_ENDPOINTS.ENROLLMENTS.BY_ID(id),
    );
    return data;
  },

  /**
   * Activate a pending enrollment (admin only)
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} Updated enrollment
   */
  async activate(id) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.ACTIVATE(id),
    );
    return data;
  },

  /**
   * Email the parent a hosted Stripe payment link for a pending enrollment (admin only)
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} { status, enrollment_id, order_id, payment_url, parent_email }
   */
  async sendPaymentLink(id) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.SEND_PAYMENT_LINK(id),
    );
    return data;
  },

  /**
   * List all invoices tied to one enrollment (admin only): original checkout,
   * backfilled months, and subscription renewals.
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} { items: [...], total }
   */
  async getInvoices(id) {
    const { data } = await apiClient.get(API_ENDPOINTS.ENROLLMENTS.INVOICES(id));
    return data;
  },

  /**
   * Enrollment counts by status for the admin page header cards.
   * @returns {Promise<Object>} { counts: {active, pending, ...}, total }
   */
  async getStats() {
    const { data } = await apiClient.get(API_ENDPOINTS.ENROLLMENTS.STATS);
    return data;
  },

  /**
   * Enrollment Review panel aggregate: child, placement, documents checklist,
   * and (owner only) payment timeline.
   * @param {string} id - Enrollment ID
   */
  async getReview(id) {
    const { data } = await apiClient.get(API_ENDPOINTS.ENROLLMENTS.REVIEW(id));
    return data;
  },

  /**
   * Re-email the parent their open hosted-invoice link (owner only).
   * @param {string} id - Enrollment ID
   */
  async sendPaymentReminder(id) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.PAYMENT_REMINDER(id),
    );
    return data;
  },

  /**
   * Join the waitlist for a full class.
   * Regular waitlist gives a 12-hour claim window when a spot opens; priority
   * waitlist auto-charges the saved card on file (requires payment_method_id).
   * @param {Object} waitlistData
   * @param {string} waitlistData.class_id - Class ID
   * @param {string} waitlistData.child_id - Child ID
   * @param {('regular'|'priority')} [waitlistData.priority='regular'] - Waitlist tier
   * @param {string} [waitlistData.payment_method_id] - Required for priority tier
   * @returns {Promise<Object>} The created waitlisted enrollment
   */
  async joinWaitlist(waitlistData) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.WAITLIST_JOIN,
      { priority: "regular", ...waitlistData },
    );
    return data;
  },

  /**
   * Claim a regular waitlist spot within the 12-hour notification window.
   * Converts the waitlisted entry to a PENDING enrollment ready for payment.
   * @param {string} id - Enrollment ID
   * @returns {Promise<Object>} The enrollment, now PENDING
   */
  async claimWaitlist(id) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.WAITLIST_CLAIM(id),
    );
    return data;
  },

  /**
   * List a class's waitlist entries (admin only).
   * Backend returns { class_id, class_name, total_waitlisted, priority_count,
   * regular_count, entries: [{ enrollment_id, child_id, child_name, class_id,
   * class_name, waitlist_priority, position, auto_promote,
   * claim_window_expires_at, created_at }] }.
   * @param {string} classId - Class ID
   * @returns {Promise<Object>} Waitlist response with an `entries` array
   */
  async getClassWaitlist(classId) {
    const { data } = await apiClient.get(
      API_ENDPOINTS.ENROLLMENTS.WAITLIST_BY_CLASS(classId),
    );
    return data;
  },

  /**
   * Manually promote a waitlisted enrollment to active (admin only).
   * @param {string} id - Enrollment ID
   * @param {Object} [options] - Promotion options
   * @param {boolean} [options.skip_payment] - Skip the payment requirement
   * @returns {Promise<Object>} The promoted enrollment
   */
  async promoteFromWaitlist(id, options = {}) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.WAITLIST_PROMOTE(id),
      options,
    );
    return data;
  },

  /**
   * Transfer enrollment to another class
   * @param {string} id - Enrollment ID
   * @param {string} newClassId - New class ID
   * @returns {Promise<Object>} Updated enrollment
   */
  /**
   * Dry run a transfer: what it costs, what blocks it, what changes on the bill.
   * Read-only on the server — safe to call on every target-class change.
   */
  async previewTransfer(id, targetClassId) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.TRANSFER_PREVIEW(id),
      { target_class_id: targetClassId },
    );
    return data;
  },

  /**
   * Execute a transfer. Moves money: credits a downgrade, charges or invoices
   * an upgrade. `overrideReason` is required (and owner-only) when the preview
   * reported blocks that are owner-overridable.
   */
  async transfer(id, newClassId, overrideReason = null) {
    const { data } = await apiClient.post(
      API_ENDPOINTS.ENROLLMENTS.TRANSFER(id),
      {
        new_class_id: newClassId,
        ...(overrideReason ? { override_reason: overrideReason } : {}),
      },
    );
    return data;
  },

  /**
   * Admin enrollment with auto-charge to parent's default payment method
   * @param {Object} enrollmentData - Enrollment information
   * @param {string} enrollmentData.child_id - Child ID
   * @param {string} enrollmentData.class_id - Class ID
   * @param {boolean} [enrollmentData.auto_charge] - Whether to auto-charge the parent's default payment method
   * @param {string} [enrollmentData.payment_option_type] - Payment option type (full_payment, monthly_subscription, etc.)
   * @param {number} [enrollmentData.amount] - Amount to charge (if different from class price)
   * @returns {Promise<Object>} Created enrollment with payment info
   */
  async adminEnroll(enrollmentData) {
    const { data } = await apiClient.post(
      "/admin/enrollments/enroll",
      enrollmentData,
    );
    return data;
  },

  /**
   * Get parent's default payment method for a child
   * @param {string} childId - Child ID
   * @returns {Promise<Object>} Parent's payment method info
   */
  async getParentPaymentMethod(childId) {
    const { data } = await apiClient.get(
      `/admin/children/${childId}/payment-method`,
    );
    return data;
  },
};

export default enrollmentsService;
